/**
 * Tracking report timezone + transform helpers.
 * Used by Tracking and TrackingForOddo pages to:
 *   - convert UTC event timestamps to a selected IANA timezone,
 *   - re-bucket events into local-day groups,
 *   - filter to the user's display range,
 *   - compute frontend counters (real calls, total duration, outgoing emails).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const getSystemTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export const SYSTEM_TIMEZONE = getSystemTimezone();

// Curated list — one or two entries per UTC offset, covering the regions our managers work in.
export const TIMEZONE_OPTIONS = [
  { value: "Pacific/Honolulu", label: "(UTC-10:00) Hawaii" },
  { value: "America/Anchorage", label: "(UTC-09:00) Alaska" },
  { value: "America/Los_Angeles", label: "(UTC-08:00) Pacific Time — Los Angeles" },
  { value: "America/Denver", label: "(UTC-07:00) Mountain Time — Denver" },
  { value: "America/Chicago", label: "(UTC-06:00) Central Time — Chicago" },
  { value: "America/New_York", label: "(UTC-05:00) Eastern Time — New York" },
  { value: "America/Halifax", label: "(UTC-04:00) Atlantic — Halifax" },
  { value: "America/Argentina/Buenos_Aires", label: "(UTC-03:00) Buenos Aires" },
  { value: "UTC", label: "(UTC+00:00) UTC" },
  { value: "Europe/London", label: "(UTC+00:00) London" },
  { value: "Europe/Paris", label: "(UTC+01:00) Paris / Berlin" },
  { value: "Europe/Warsaw", label: "(UTC+01:00) Warsaw" },
  { value: "Europe/Kyiv", label: "(UTC+02:00) Kyiv" },
  { value: "Africa/Cairo", label: "(UTC+02:00) Cairo" },
  { value: "Europe/Moscow", label: "(UTC+03:00) Moscow" },
  { value: "Asia/Dubai", label: "(UTC+04:00) Dubai" },
  { value: "Asia/Kolkata", label: "(UTC+05:30) India — Kolkata" },
  { value: "Asia/Bangkok", label: "(UTC+07:00) Bangkok" },
  { value: "Asia/Singapore", label: "(UTC+08:00) Singapore / Hong Kong" },
  { value: "Asia/Tokyo", label: "(UTC+09:00) Tokyo / Seoul" },
  { value: "Australia/Sydney", label: "(UTC+10:00) Sydney" },
  { value: "Pacific/Auckland", label: "(UTC+12:00) Auckland" },
];

// Ensure system TZ is always selectable even if it's not in the curated list.
export const getTimezoneOptions = () => {
  const has = TIMEZONE_OPTIONS.some((o) => o.value === SYSTEM_TIMEZONE);
  if (has) return TIMEZONE_OPTIONS;
  return [{ value: SYSTEM_TIMEZONE, label: `(System) ${SYSTEM_TIMEZONE}` }, ...TIMEZONE_OPTIONS];
};

const toDate = (input) => {
  if (input instanceof Date) return input;
  if (typeof input !== "string" || !input) return null;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
};

// Returns "YYYY-MM-DD" for the given instant in the target timezone.
export const getZonedDateKey = (input, timezone) => {
  const date = toDate(input);
  if (!date) return null;
  // en-CA gives ISO-like YYYY-MM-DD output.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

// Returns "HH:MM" for the given instant in the target timezone.
export const getZonedTime = (input, timezone) => {
  const date = toDate(input);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

/**
 * Returns a UTC-ms timestamp such that getUTC*() methods return the wall time
 * the instant has in `timezone`. Used to feed ApexCharts' UTC-based xaxis so
 * its existing UTC-formatter prints the target timezone's wall clock.
 */
export const toZonedUtcMs = (input, timezone) => {
  const date = toDate(input);
  if (!date) return NaN;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const o = {};
  for (const p of parts) o[p.type] = p.value;
  // Some locales emit "24" for hour at midnight — normalize.
  const hour = o.hour === "24" ? "00" : o.hour;
  return Date.UTC(
    Number(o.year),
    Number(o.month) - 1,
    Number(o.day),
    Number(hour),
    Number(o.minute),
    Number(o.second),
  );
};

// Day boundaries for the chart, expressed in the same shifted-UTC scale as toZonedUtcMs output.
export const getZonedDayBoundsMs = (dateKey) => {
  const [y, m, d] = dateKey.split("-").map(Number);
  const start = Date.UTC(y, m - 1, d, 0, 0, 0);
  return { start, end: start + DAY_MS };
};

// Add/subtract whole days from a "YYYY-MM-DD" string.
export const shiftDateKey = (dateKey, days) => {
  const [y, m, d] = dateKey.split("-").map(Number);
  const base = Date.UTC(y, m - 1, d);
  const shifted = new Date(base + days * DAY_MS);
  const yy = shifted.getUTCFullYear();
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(shifted.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// "HH:MM:SS" (or "MM:SS") -> total seconds. Returns 0 on parse failure.
const parseDurationToSec = (str) => {
  if (!str || typeof str !== "string") return 0;
  const parts = str.split(":").map((p) => Number(p));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
};

const formatSecToDuration = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

/**
 * Counters computed from the in-memory employee data after timezone re-bucketing.
 * "Real call" definition: any call with a parseable duration > 0. Confirm with team
 * if the rule should be stricter (e.g. only connected outbound + answered inbound).
 */
export const computeCounters = (empData) => {
  const calls = empData.calls || [];
  const realCalls = calls.filter((c) => parseDurationToSec(c.duration) > 0);
  const totalRealCalls = realCalls.length;
  const totalSec = realCalls.reduce((acc, c) => acc + parseDurationToSec(c.duration), 0);
  return {
    totalRealCalls,
    totalCallsDuration: formatSecToDuration(totalSec),
    totalEmails: (empData.emails || []).length,
  };
};

const EVENT_DESCRIPTORS = [
  { src: "calls", dateField: "call_date_time" },
  { src: "emails", dateField: "sent_date" },
  { src: "chat_bids", dateField: "call_date_time" },
  { src: "timetracking", dateField: "clock_in" },
];

const makeEmpBucket = (empData) => ({
  first_name: empData.first_name,
  last_name: empData.last_name,
  scheduled_shift_start: empData.scheduled_shift_start,
  scheduled_shift_end: empData.scheduled_shift_end,
  calls: [],
  emails: [],
  chat_bids: [],
  timetracking: [],
});

/**
 * Re-bucket the raw report into the user's selected timezone, filter to displayRange,
 * and attach computed counters. Output keeps the same shape the existing UI expects:
 *   [ { "<localDay>": { "<empId>": { ...employeeData } } }, ... ]
 *
 * @param {Array}  rawReport     Raw API response (already an array of date-keyed groups).
 * @param {string} timezone      IANA tz string.
 * @param {object} displayRange  { from: "YYYY-MM-DD", to: "YYYY-MM-DD" } user-picked range.
 */
export const transformReport = (rawReport, timezone, displayRange) => {
  if (!Array.isArray(rawReport) || rawReport.length === 0) return rawReport;
  if (!displayRange?.from || !displayRange?.to) return rawReport;

  const buckets = new Map(); // localDay -> Map<empId, empBucket>

  const stash = (empId, empData, localDay, srcKey, item) => {
    if (!buckets.has(localDay)) buckets.set(localDay, new Map());
    const empMap = buckets.get(localDay);
    if (!empMap.has(empId)) empMap.set(empId, makeEmpBucket(empData));
    empMap.get(empId)[srcKey].push(item);
  };

  for (const dayObj of rawReport) {
    if (!dayObj || typeof dayObj !== "object") continue;
    for (const byEmp of Object.values(dayObj)) {
      if (!byEmp || typeof byEmp !== "object") continue;
      for (const [empId, empData] of Object.entries(byEmp)) {
        if (!empData || typeof empData !== "object") continue;
        for (const { src, dateField } of EVENT_DESCRIPTORS) {
          const list = empData[src];
          if (!Array.isArray(list)) continue;
          for (const item of list) {
            const ts = item?.[dateField];
            if (!ts) continue;
            const localDay = getZonedDateKey(ts, timezone);
            if (!localDay) continue;
            if (localDay < displayRange.from || localDay > displayRange.to) continue;
            stash(empId, empData, localDay, src, item);
          }
        }
      }
    }
  }

  // Compute counters per (day, employee) on the in-range data.
  for (const empMap of buckets.values()) {
    for (const empData of empMap.values()) {
      const c = computeCounters(empData);
      empData.total_real_calls_computed = c.totalRealCalls;
      empData.total_calls_duration_computed = c.totalCallsDuration;
      empData.total_emails_computed = c.totalEmails;
    }
  }

  return Array.from(buckets.keys())
    .sort()
    .map((day) => ({ [day]: Object.fromEntries(buckets.get(day)) }));
};
